import { Link } from 'react-router';
import { useHome } from '../api/hooks.js';
import { Card, CardHead } from '../components/Card.js';
import { cx } from '../components/classes.js';
import { ChevronRight } from '../components/Icons.js';
import { SkeletonRows } from '../components/Notes.js';
import { useSearchShell } from '../components/SearchShell.js';
import { StatusGlyph } from '../components/StatusGlyph.js';
import { formatRecency, titleCase } from '../lib/format.js';
import type { Verification } from '../lib/types.js';

export function recordStatus(r: Verification): 'pass' | 'attention' | 'fail' | 'unknown' {
  if (!r.entity) return 'unknown';
  if (r.result.failed > 0) return 'fail';
  if (r.result.attention > 0 || r.result.unknown > 0) return 'attention';
  return 'pass';
}

export function recordTitle(r: Verification): string {
  return r.entity ? titleCase(r.entity.name) : `“${r.subject}” — not on the register`;
}

function RecordLine({ record }: { record: Verification }) {
  return (
    <Link
      to={`/records/${record.id}`}
      className="flex items-center gap-3.5 border-t border-line px-5 py-4 hover:bg-sunk/60"
    >
      <StatusGlyph status={recordStatus(record)} />
      <span className="flex grow flex-col gap-0.5">
        <span className="text-[15px] font-medium">{recordTitle(record)}</span>
        <span className="text-[13px] text-mute">
          {record.entity ? 'Record issued' : 'No-match record'} ·{' '}
          {formatRecency(record.issued_at)}
        </span>
      </span>
      <ChevronRight className="text-mute" />
    </Link>
  );
}

/**
 * Wireframe §3: below the (SearchShell-owned) search box, what you did and
 * what needs you. On submit the shell flags `leaving` and these cards fade
 * out before the URL changes to `/search`.
 */
export function Home() {
  const { leaving } = useSearchShell();
  const home = useHome();

  const records = home.data?.records ?? [];
  const pending = home.data?.review_pending ?? 0;

  return (
    <div
      data-testid="home-cards"
      className={cx(
        'mx-auto grid w-[760px] max-w-full grid-cols-2 gap-6 transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none',
        leaving ? 'translate-y-2 opacity-0' : 'translate-y-0 opacity-100',
      )}
    >
      <Card>
        <CardHead>Your recent records</CardHead>
        {home.isPending ? (
          <SkeletonRows rows={3} />
        ) : records.length === 0 ? (
          <div className="border-t border-line px-5 py-5 text-[13px] text-mute">
            Nothing yet. Records you issue appear here.
          </div>
        ) : (
          records.slice(0, 4).map((r) => <RecordLine key={r.id} record={r} />)
        )}
      </Card>
      <Card>
        <CardHead>Needs your attention</CardHead>
        {home.isPending ? (
          <SkeletonRows rows={2} />
        ) : pending > 0 ? (
          <Link
            to="/review"
            className="flex items-center gap-3.5 border-t border-line px-5 py-4 hover:bg-sunk/60"
          >
            <StatusGlyph status="unknown" label="Review" />
            <span className="flex grow flex-col gap-0.5">
              <span className="text-[15px] font-medium">
                {pending} match{pending === 1 ? '' : 'es'} awaiting your review
              </span>
              <span className="text-[13px] text-mute">
                Two records scored close — a person decides
              </span>
            </span>
            <ChevronRight className="text-mute" />
          </Link>
        ) : (
          <div className="border-t border-line px-5 py-5 text-[13px] text-mute">
            Nothing needs you right now. Alerts on watched companies arrive with Watch.
          </div>
        )}
      </Card>
    </div>
  );
}
